import { useState } from 'react';
import { UserPlus, Plus, Trash2, Save, Wand2, ChevronDown, ChevronUp, X, Check, Loader2, Play } from 'lucide-react';
import { savePersona } from '../../../services/firestore/pjeService';
import type { PersonaData } from './PersonaCard';
import {
  runSingleTwinV3,
  type V3RunProgress,
} from '../../../services/patientJourney/twinService';
import type { TestPersona, BehaviorModel, Touchpoint } from '../../../types/patientJourney';
import { DEFAULT_CONTEXT } from '../../../types/patientJourney';

// ── CONSTANTS ──

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN',
  'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
  'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
  'VT','VA','WA','WV','WI','WY',
];

const VERTICALS = [
  "Women's Hormones", "Men's Hormones", 'Thyroid', 'Metabolic', 'Fertility', 'General Wellness',
];

const MED_CATEGORIES = ['SSRI', 'Blood thinner', 'Statin', 'PPI', 'Thyroid med', 'Diabetes med', 'Other'];

// ── CONTEXT MODIFIER SCHEMA ──

interface ContextModifierGroup {
  label: string;
  fields: { key: string; label: string; options: string[] }[];
}

const CONTEXT_GROUPS: ContextModifierGroup[] = [
  {
    label: 'Temporal',
    fields: [
      { key: 'timeOfDay', label: 'Time of Day', options: ['morning', 'afternoon', 'evening', 'late_night'] },
      { key: 'cyclePhase', label: 'Cycle Phase', options: ['follicular', 'ovulatory', 'luteal', 'menstrual', 'postmenopausal', 'not_applicable'] },
      { key: 'season', label: 'Season', options: ['spring', 'summer', 'fall', 'winter'] },
      { key: 'timeSinceLast', label: 'Time Since Last', options: ['first_visit', 'days', 'weeks', 'months'] },
    ],
  },
  {
    label: 'Emotional',
    fields: [
      { key: 'baselineStress', label: 'Baseline Stress', options: ['low', 'moderate', 'high', 'crisis'] },
      { key: 'recentLifeEvent', label: 'Recent Life Event', options: ['none', 'divorce', 'job_loss', 'bereavement', 'new_diagnosis', 'new_baby', 'relocation'] },
      { key: 'motivation', label: 'Motivation', options: ['desperate', 'concerned', 'curious', 'proactive', 'skeptical'] },
    ],
  },
  {
    label: 'Relational',
    fields: [
      { key: 'partnerStatus', label: 'Partner Status', options: ['single', 'partnered', 'divorced', 'widowed'] },
      { key: 'caregiverBurden', label: 'Caregiver Burden', options: ['none', 'light', 'heavy'] },
      { key: 'socialSupport', label: 'Social Support', options: ['strong', 'moderate', 'isolated'] },
    ],
  },
  {
    label: 'Financial',
    fields: [
      { key: 'budgetSensitivity', label: 'Budget Sensitivity', options: ['low', 'moderate', 'high', 'severe'] },
      { key: 'insurance', label: 'Insurance', options: ['insured', 'uninsured', 'hsa_fsa_eligible'] },
    ],
  },
  {
    label: 'Literacy',
    fields: [
      { key: 'healthKnowledge', label: 'Health Knowledge', options: ['minimal', 'basic', 'informed', 'expert'] },
      { key: 'supplementExperience', label: 'Supplement Experience', options: ['none', 'some', 'extensive'] },
      { key: 'aiComfort', label: 'AI Comfort', options: ['distrustful', 'cautious', 'neutral', 'comfortable'] },
    ],
  },
  {
    label: 'Clinical',
    fields: [
      { key: 'journeyStage', label: 'Journey Stage', options: ['first_visit', 'on_protocol', 'retesting', 'post_escalation', 're_engagement'] },
      { key: 'adherenceLevel', label: 'Adherence Level', options: ['high', 'moderate', 'low', 'unknown'] },
    ],
  },
];

// ── TYPES ──

interface LabRow {
  id: string;
  biomarker: string;
  value: string;
  unit: string;
}

interface MedRow {
  id: string;
  name: string;
  dose: string;
  category: string;
  duration: string;
}

interface PatientPersona {
  demographics: {
    name: string;
    age: string;
    sex: string;
    state: string;
    zip: string;
  };
  primaryVertical: string;
  secondaryVerticals: string[];
  labs: LabRow[];
  medications: MedRow[];
  conditions: string[];
  contraindications: string[];
  allergens: string[];
  contextModifiers: Record<string, string>;
}

// ── HELPERS ──

let _uid = 0;
function uid() {
  return `_pb_${++_uid}_${Date.now()}`;
}

function getLabStatus(value: string): { label: string; color: string } {
  const num = parseFloat(value);
  if (isNaN(num)) return { label: '--', color: 'text-text-muted' };
  // Placeholder status logic — real engine would use biomarker ranges
  return { label: 'Entered', color: 'text-text-secondary' };
}

// ── MAIN COMPONENT ──

export function PatientBuilder() {
  const [toast, setToast] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['demographics', 'vertical'])
  );

  const [persona, setPersona] = useState<PatientPersona>({
    demographics: { name: '', age: '', sex: '', state: '', zip: '' },
    primaryVertical: '',
    secondaryVerticals: [],
    labs: [],
    medications: [],
    conditions: [],
    contraindications: [],
    allergens: [],
    contextModifiers: {},
  });

  const [isRunning, setIsRunning] = useState(false);
  const [runProgress, setRunProgress] = useState<string>('');
  const [runResult, setRunResult] = useState<{ passRate: number; messageCount: number } | null>(null);

  // Toast auto-dismiss
  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  // Section expand/collapse
  function toggleSection(section: string) {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  }

  // Demographics
  function updateDemo<K extends keyof PatientPersona['demographics']>(key: K, value: string) {
    setPersona((p) => ({ ...p, demographics: { ...p.demographics, [key]: value } }));
  }

  // Vertical
  function toggleSecondaryVertical(v: string) {
    setPersona((p) => {
      const svs = p.secondaryVerticals.includes(v)
        ? p.secondaryVerticals.filter((x) => x !== v)
        : [...p.secondaryVerticals, v];
      return { ...p, secondaryVerticals: svs };
    });
  }

  // Labs
  function addLab() {
    setPersona((p) => ({
      ...p,
      labs: [...p.labs, { id: uid(), biomarker: '', value: '', unit: '' }],
    }));
  }
  function updateLab(id: string, field: keyof LabRow, value: string) {
    setPersona((p) => ({
      ...p,
      labs: p.labs.map((l) => (l.id === id ? { ...l, [field]: value } : l)),
    }));
  }
  function removeLab(id: string) {
    setPersona((p) => ({ ...p, labs: p.labs.filter((l) => l.id !== id) }));
  }

  // Medications
  function addMed() {
    setPersona((p) => ({
      ...p,
      medications: [...p.medications, { id: uid(), name: '', dose: '', category: '', duration: '' }],
    }));
  }
  function updateMed(id: string, field: keyof MedRow, value: string) {
    setPersona((p) => ({
      ...p,
      medications: p.medications.map((m) => (m.id === id ? { ...m, [field]: value } : m)),
    }));
  }
  function removeMed(id: string) {
    setPersona((p) => ({ ...p, medications: p.medications.filter((m) => m.id !== id) }));
  }

  // Tags (conditions, contraindications, allergens)
  function addTag(field: 'conditions' | 'contraindications' | 'allergens', value: string) {
    const trimmed = value.trim();
    if (!trimmed) return;
    setPersona((p) => {
      if (p[field].includes(trimmed)) return p;
      return { ...p, [field]: [...p[field], trimmed] };
    });
  }
  function removeTag(field: 'conditions' | 'contraindications' | 'allergens', value: string) {
    setPersona((p) => ({ ...p, [field]: p[field].filter((t) => t !== value) }));
  }

  // Context modifiers
  function updateContext(key: string, value: string) {
    setPersona((p) => ({
      ...p,
      contextModifiers: { ...p.contextModifiers, [key]: value },
    }));
  }

  // Actions
  async function handleSavePersona() {
    try {
      const personaData: PersonaData = {
        personaId: `pje_custom_${Date.now()}`,
        name: persona.demographics.name || 'Unnamed Persona',
        archetype: 'Custom',
        demographics: {
          name: persona.demographics.name,
          age: parseInt(persona.demographics.age, 10) || 0,
          sex: persona.demographics.sex,
          state: persona.demographics.state,
        },
        labs: persona.labs.map((lab) => ({
          biomarker: lab.biomarker,
          value: parseFloat(lab.value) || 0,
          unit: lab.unit,
          conventionalRange: '',
          functionalRange: '',
          status: 'optimal',
          trigger: false,
        })),
        medications: persona.medications.map((med) => ({
          name: med.name,
          dose: med.dose,
          category: med.category,
          duration: med.duration,
        })),
        conditions: persona.conditions,
        contraindications: persona.contraindications,
        allergens: persona.allergens,
        validationChecks: [],
      };

      await savePersona(personaData, persona.primaryVertical);
      showToast('Persona saved successfully');
    } catch (err) {
      console.error('Failed to save persona:', err);
      showToast('Failed to save persona');
    }
  }

  function handleGeneratePanel() {
    alert('Panel generation ready — connect to engine');
  }

  async function handleSaveAndRun() {
    // First save
    await handleSavePersona();

    setIsRunning(true);
    setRunResult(null);
    setRunProgress('Building persona...');

    try {
      const personaId = `pje_custom_${Date.now()}`;

      // Map form context modifiers to ContextLayer, falling back to DEFAULT_CONTEXT
      const ctx = persona.contextModifiers;
      const contextModifiers = {
        ...DEFAULT_CONTEXT,
        ...(ctx.timeOfDay && { timeOfDay: ctx.timeOfDay as typeof DEFAULT_CONTEXT.timeOfDay }),
        ...(ctx.cyclePhase && { cyclePhase: ctx.cyclePhase as typeof DEFAULT_CONTEXT.cyclePhase }),
        ...(ctx.season && { season: ctx.season as typeof DEFAULT_CONTEXT.season }),
        ...(ctx.timeSinceLast && { timeSinceLastInteraction: ctx.timeSinceLast as typeof DEFAULT_CONTEXT.timeSinceLastInteraction }),
        ...(ctx.baselineStress && { baselineStress: ctx.baselineStress as typeof DEFAULT_CONTEXT.baselineStress }),
        ...(ctx.recentLifeEvent && { recentLifeEvent: ctx.recentLifeEvent as typeof DEFAULT_CONTEXT.recentLifeEvent }),
        ...(ctx.motivation && { motivation: ctx.motivation as typeof DEFAULT_CONTEXT.motivation }),
        ...(ctx.partnerStatus && { partnerStatus: ctx.partnerStatus as typeof DEFAULT_CONTEXT.partnerStatus }),
        ...(ctx.caregiverBurden && { caregiverBurden: ctx.caregiverBurden as typeof DEFAULT_CONTEXT.caregiverBurden }),
        ...(ctx.socialSupport && { socialSupport: ctx.socialSupport as typeof DEFAULT_CONTEXT.socialSupport }),
        ...(ctx.budgetSensitivity && { budgetSensitivity: ctx.budgetSensitivity as typeof DEFAULT_CONTEXT.budgetSensitivity }),
        ...(ctx.insurance && { insuranceContext: ctx.insurance as typeof DEFAULT_CONTEXT.insuranceContext }),
        ...(ctx.healthKnowledge && { healthKnowledge: ctx.healthKnowledge as typeof DEFAULT_CONTEXT.healthKnowledge }),
        ...(ctx.supplementExperience && { supplementExperience: ctx.supplementExperience as typeof DEFAULT_CONTEXT.supplementExperience }),
        ...(ctx.aiComfort && { aiComfort: ctx.aiComfort as typeof DEFAULT_CONTEXT.aiComfort }),
        ...(ctx.journeyStage && { journeyStage: ctx.journeyStage as typeof DEFAULT_CONTEXT.journeyStage }),
        ...(ctx.adherenceLevel && { adherenceLevel: ctx.adherenceLevel as typeof DEFAULT_CONTEXT.adherenceLevel }),
      };

      // Build TestPersona from form state
      const testPersona: TestPersona = {
        personaId,
        name: persona.demographics.name || 'Custom Persona',
        archetype: 'Custom',
        demographics: {
          name: persona.demographics.name,
          age: parseInt(persona.demographics.age, 10) || 30,
          sex: (persona.demographics.sex as 'Male' | 'Female') || 'Female',
          state: persona.demographics.state,
          zipCode: persona.demographics.zip || undefined,
        },
        labs: persona.labs.map((lab) => ({
          biomarker: lab.biomarker,
          value: parseFloat(lab.value) || 0,
          unit: lab.unit,
          conventionalRange: '',
          functionalRange: '',
          status: 'optimal' as const,
          trigger: false,
          timestamp: new Date().toISOString(),
        })),
        medications: persona.medications.map((med) => ({
          name: med.name,
          dose: med.dose,
          category: med.category,
          duration: med.duration,
        })),
        conditions: persona.conditions,
        contraindications: persona.contraindications,
        allergens: persona.allergens,
        contextModifiers,
        journeyTouchpoints: [],
        validationChecklist: [],
      };

      // Build default behavior
      const behavior: BehaviorModel = {
        communicationStyle: 'conversational',
        questionFrequency: 'moderate',
        attentionSpan: 'medium',
        decisionSpeed: 'moderate',
        skepticismLevel: 30,
        complianceTendency: 70,
        priceResistance: 40,
        emotionalExpressiveness: 50,
        objectionPatterns: [],
        dataSource: 'manual',
        dataVersion: 1,
      };

      // Build default touchpoint
      const touchpoint: Touchpoint = {
        touchpointId: `tp-custom-${personaId}`,
        type: 'first_contact',
        order: 1,
        label: 'First Contact — Lab Upload',
        trigger: 'Patient uploads lab results for the first time',
        conversationScript: [],
        validationChecklist: [],
      };

      setRunProgress('Running v3.1 twin conversation...');

      const result = await runSingleTwinV3(
        testPersona,
        behavior,
        touchpoint,
        20,
        (prog: V3RunProgress) => {
          if (prog.status === 'running') {
            setRunProgress(
              `Turn ${prog.currentTurn}/${prog.maxTurns} — ${prog.phase === 'phase1_scripted' ? 'Phase 1' : 'Phase 2'} (${prog.activeRegister || 'blended'})`,
            );
          } else if (prog.status === 'scoring') {
            setRunProgress('Scoring conversation...');
          }
        },
      );

      setRunResult({
        passRate: result.passRate,
        messageCount: result.conversationLog.length,
      });
      setRunProgress('');
    } catch (err) {
      setRunProgress(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsRunning(false);
    }
  }

  return (
    <div className="max-w-5xl">
      {/* Toast */}
        {toast && (
          <div
            className="fixed top-4 right-4 z-50 bg-bg-elevated border border-bg-tertiary rounded-lg px-4 py-3 text-base shadow-lg flex items-center gap-2"
          >
            <Check size={16} className="text-optimal" />
            {toast}
            <button onClick={() => setToast(null)} className="ml-2 text-text-muted hover:text-text-primary">
              <X size={14} />
            </button>
          </div>
        )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-lg bg-gold/10">
            <UserPlus size={22} className="text-gold" />
          </div>
          <div>
            <h2 className="text-3xl font-display text-gold">Custom Patient Builder</h2>
            <p className="text-base text-text-muted">Construct a patient profile from scratch</p>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-4">
        {/* ── DEMOGRAPHICS ── */}
        <SectionCard
          title="Demographics"
          id="demographics"
          expanded={expandedSections.has('demographics')}
          onToggle={toggleSection}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label="Name">
              <input
                type="text"
                value={persona.demographics.name}
                onChange={(e) => updateDemo('name', e.target.value)}
                placeholder="e.g. Sarah Mitchell"
                className="input-field"
              />
            </Field>
            <Field label="Age">
              <input
                type="number"
                value={persona.demographics.age}
                onChange={(e) => updateDemo('age', e.target.value)}
                placeholder="e.g. 42"
                min={0}
                max={120}
                className="input-field"
              />
            </Field>
            <Field label="Sex">
              <select
                value={persona.demographics.sex}
                onChange={(e) => updateDemo('sex', e.target.value)}
                className="input-field"
              >
                <option value="">Select...</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </Field>
            <Field label="State">
              <select
                value={persona.demographics.state}
                onChange={(e) => updateDemo('state', e.target.value)}
                className="input-field"
              >
                <option value="">Select...</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </Field>
            <Field label="Zip (optional)">
              <input
                type="text"
                value={persona.demographics.zip}
                onChange={(e) => updateDemo('zip', e.target.value)}
                placeholder="e.g. 90210"
                maxLength={10}
                className="input-field"
              />
            </Field>
          </div>
        </SectionCard>

        {/* ── VERTICAL ── */}
        <SectionCard
          title="Vertical"
          id="vertical"
          expanded={expandedSections.has('vertical')}
          onToggle={toggleSection}
        >
          <div className="space-y-4">
            <Field label="Primary Vertical">
              <select
                value={persona.primaryVertical}
                onChange={(e) => setPersona((p) => ({ ...p, primaryVertical: e.target.value }))}
                className="input-field"
              >
                <option value="">Select...</option>
                {VERTICALS.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </Field>
            <Field label="Secondary Verticals">
              <div className="flex flex-wrap gap-2 mt-1">
                {VERTICALS.filter((v) => v !== persona.primaryVertical).map((v) => {
                  const active = persona.secondaryVerticals.includes(v);
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => toggleSecondaryVertical(v)}
                      className={`px-3 py-2 rounded-lg text-base border transition-colors ${
                        active
                          ? 'bg-gold/15 border-gold/40 text-gold'
                          : 'bg-bg-elevated border-bg-tertiary text-text-secondary hover:border-gold/20'
                      }`}
                    >
                      {v}
                    </button>
                  );
                })}
              </div>
            </Field>
          </div>
        </SectionCard>

        {/* ── LAB VALUES ── */}
        <SectionCard
          title="Lab Values"
          id="labs"
          expanded={expandedSections.has('labs')}
          onToggle={toggleSection}
        >
          <div className="space-y-3">
            {persona.labs.length === 0 && (
              <p className="text-base text-text-muted py-2">No lab values added yet.</p>
            )}
            {persona.labs.map((lab) => {
              const status = getLabStatus(lab.value);
              return (
                <div
                  key={lab.id}
                  className="grid grid-cols-1 sm:grid-cols-[1fr_100px_80px_80px_40px] gap-2 items-end bg-bg-elevated rounded-lg p-3"
                >
                  <Field label="Biomarker">
                    <input
                      type="text"
                      value={lab.biomarker}
                      onChange={(e) => updateLab(lab.id, 'biomarker', e.target.value)}
                      placeholder="e.g. TSH"
                      className="input-field"
                    />
                  </Field>
                  <Field label="Value">
                    <input
                      type="number"
                      value={lab.value}
                      onChange={(e) => updateLab(lab.id, 'value', e.target.value)}
                      placeholder="0.0"
                      step="any"
                      className="input-field"
                    />
                  </Field>
                  <Field label="Unit">
                    <input
                      type="text"
                      value={lab.unit}
                      onChange={(e) => updateLab(lab.id, 'unit', e.target.value)}
                      placeholder="mIU/L"
                      className="input-field"
                    />
                  </Field>
                  <div className="flex items-center justify-center pb-1">
                    <span className={`text-base ${status.color}`}>{status.label}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeLab(lab.id)}
                    className="p-2 rounded-lg hover:bg-bg-tertiary text-text-muted hover:text-critical transition-colors self-end"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              );
            })}
            <div className="flex flex-wrap gap-3 pt-2">
              <button
                type="button"
                onClick={addLab}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-bg-elevated border border-bg-tertiary rounded-lg text-base text-text-secondary hover:border-gold/30 hover:text-gold transition-colors"
              >
                <Plus size={16} />
                Add Lab
              </button>
              <button
                type="button"
                onClick={handleGeneratePanel}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-gold/10 border border-gold/20 rounded-lg text-base text-gold hover:bg-gold/20 transition-colors"
              >
                <Wand2 size={16} />
                Generate Full Panel
              </button>
            </div>
          </div>
        </SectionCard>

        {/* ── MEDICATIONS ── */}
        <SectionCard
          title="Medications"
          id="medications"
          expanded={expandedSections.has('medications')}
          onToggle={toggleSection}
        >
          <div className="space-y-3">
            {persona.medications.length === 0 && (
              <p className="text-base text-text-muted py-2">No medications added yet.</p>
            )}
            {persona.medications.map((med) => (
              <div
                key={med.id}
                className="grid grid-cols-1 sm:grid-cols-[1fr_100px_140px_100px_40px] gap-2 items-end bg-bg-elevated rounded-lg p-3"
              >
                <Field label="Name">
                  <input
                    type="text"
                    value={med.name}
                    onChange={(e) => updateMed(med.id, 'name', e.target.value)}
                    placeholder="e.g. Metformin"
                    className="input-field"
                  />
                </Field>
                <Field label="Dose">
                  <input
                    type="text"
                    value={med.dose}
                    onChange={(e) => updateMed(med.id, 'dose', e.target.value)}
                    placeholder="500mg"
                    className="input-field"
                  />
                </Field>
                <Field label="Category">
                  <select
                    value={med.category}
                    onChange={(e) => updateMed(med.id, 'category', e.target.value)}
                    className="input-field"
                  >
                    <option value="">Select...</option>
                    {MED_CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Duration">
                  <input
                    type="text"
                    value={med.duration}
                    onChange={(e) => updateMed(med.id, 'duration', e.target.value)}
                    placeholder="6 months"
                    className="input-field"
                  />
                </Field>
                <button
                  type="button"
                  onClick={() => removeMed(med.id)}
                  className="p-2 rounded-lg hover:bg-bg-tertiary text-text-muted hover:text-critical transition-colors self-end"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addMed}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-bg-elevated border border-bg-tertiary rounded-lg text-base text-text-secondary hover:border-gold/30 hover:text-gold transition-colors"
            >
              <Plus size={16} />
              Add Medication
            </button>
          </div>
        </SectionCard>

        {/* ── CONDITIONS ── */}
        <SectionCard
          title="Conditions"
          id="conditions"
          expanded={expandedSections.has('conditions')}
          onToggle={toggleSection}
        >
          <TagInput
            tags={persona.conditions}
            onAdd={(v) => addTag('conditions', v)}
            onRemove={(v) => removeTag('conditions', v)}
            placeholder="Type a condition and press Enter..."
          />
        </SectionCard>

        {/* ── CONTRAINDICATIONS ── */}
        <SectionCard
          title="Contraindications"
          id="contraindications"
          expanded={expandedSections.has('contraindications')}
          onToggle={toggleSection}
        >
          <TagInput
            tags={persona.contraindications}
            onAdd={(v) => addTag('contraindications', v)}
            onRemove={(v) => removeTag('contraindications', v)}
            placeholder="Type a contraindication and press Enter..."
          />
        </SectionCard>

        {/* ── ALLERGENS ── */}
        <SectionCard
          title="Allergens"
          id="allergens"
          expanded={expandedSections.has('allergens')}
          onToggle={toggleSection}
        >
          <TagInput
            tags={persona.allergens}
            onAdd={(v) => addTag('allergens', v)}
            onRemove={(v) => removeTag('allergens', v)}
            placeholder="Type an allergen and press Enter..."
          />
        </SectionCard>

        {/* ── CONTEXT MODIFIERS ── */}
        <SectionCard
          title="Context Modifiers"
          id="context"
          expanded={expandedSections.has('context')}
          onToggle={toggleSection}
        >
          <div className="space-y-6">
            {CONTEXT_GROUPS.map((group) => (
              <div key={group.label}>
                <p className="text-base font-semibold text-gold mb-3">{group.label}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {group.fields.map((field) => (
                    <Field key={field.key} label={field.label}>
                      <select
                        value={persona.contextModifiers[field.key] || ''}
                        onChange={(e) => updateContext(field.key, e.target.value)}
                        className="input-field"
                      >
                        <option value="">Select...</option>
                        {field.options.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt.replace(/_/g, ' ')}
                          </option>
                        ))}
                      </select>
                    </Field>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      {/* ── BOTTOM ACTIONS ── */}
      <div className="sticky bottom-0 mt-6 py-4 bg-bg-deep border-t border-bg-tertiary flex flex-wrap items-center justify-end gap-3">
        <button
          onClick={handleSavePersona}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gold/10 border border-gold/20 text-gold rounded-lg text-base font-medium hover:bg-gold/20 transition-colors"
        >
          <Save size={16} />
          Save Persona
        </button>
        <button
          onClick={handleSaveAndRun}
          disabled={isRunning}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-purple-600 text-white font-medium text-base hover:bg-purple-500 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isRunning ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Running...
            </>
          ) : (
            <>
              <Play size={16} />
              Save &amp; Run Twin
            </>
          )}
        </button>
      </div>

      {/* ── RUN PROGRESS / RESULTS ── */}
      {(runProgress || runResult) && (
        <div className="mt-3 bg-bg-elevated border border-bg-tertiary rounded-lg px-4 py-3">
          {runProgress && (
            <p className="text-sm text-text-secondary">{runProgress}</p>
          )}
          {runResult && !isRunning && (
            <div className="flex items-center gap-4">
              <span className={`text-sm font-semibold ${
                runResult.passRate >= 85 ? 'text-emerald-400' : runResult.passRate >= 70 ? 'text-amber-400' : 'text-critical'
              }`}>
                Pass Rate: {runResult.passRate}%
              </span>
              <span className="text-sm text-text-muted">
                {runResult.messageCount} messages
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── REUSABLE SUB-COMPONENTS ──

function SectionCard({
  title,
  id,
  expanded,
  onToggle,
  children,
}: {
  title: string;
  id: string;
  expanded: boolean;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-bg-elevated transition-colors"
      >
        <h3 className="text-base font-semibold text-text-secondary uppercase tracking-wider">{title}</h3>
        {expanded ? (
          <ChevronUp size={16} className="text-text-muted" />
        ) : (
          <ChevronDown size={16} className="text-text-muted" />
        )}
      </button>
        {expanded && (
          <div className="px-5 pb-5">{children}</div>
        )}
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-text-muted mb-1.5">
        {label}
        {required && <span className="text-critical ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}

function TagInput({
  tags,
  onAdd,
  onRemove,
  placeholder,
}: {
  tags: string[];
  onAdd: (value: string) => void;
  onRemove: (value: string) => void;
  placeholder: string;
}) {
  const [input, setInput] = useState('');

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      onAdd(input);
      setInput('');
    }
    if (e.key === 'Backspace' && input === '' && tags.length > 0) {
      onRemove(tags[tags.length - 1]);
    }
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        {tags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gold/10 border border-gold/20 rounded-lg text-base text-gold"
          >
            {tag}
            <button
              type="button"
              onClick={() => onRemove(tag)}
              className="text-gold/60 hover:text-gold transition-colors"
            >
              <X size={14} />
            </button>
          </span>
        ))}
      </div>
      <input
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="input-field"
      />
    </div>
  );
}
